// src/utils/posAssignmentHistory.js
import { db } from "../firebase/firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";

/* ------------------------------------------------------------------
   SAFE DATE HELPERS (Firestore Timestamp / Date / "YYYY-MM-DD")
------------------------------------------------------------------- */
const toJsDate = (v) => {
  if (!v) return null;

  // Firestore Timestamp
  if (typeof v?.toDate === "function") return v.toDate();

  // JS Date
  if (v instanceof Date) return v;

  // "YYYY-MM-DD" => local midnight
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  // fallback
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const endOfDay = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};

/* ------------------------------------------------------------------
   Returns FULL assignment object
   { vehicleId, deviceCompany, startDate, endDate, ... }
------------------------------------------------------------------- */
export async function getAssignmentForDate(terminalId, transactionDate) {
  if (!terminalId || !transactionDate) return null;

  try {
    // IMPORTANT: Firestore doc ID must match exactly
    const terminalKey = String(terminalId).trim();

    const assignmentRef = doc(db, "posDeviceAssignments", terminalKey);
    const snap = await getDoc(assignmentRef, { source: "server" });

    if (!snap.exists()) return null;

    const data = snap.data() || {};
    const assignments = Array.isArray(data.assignments) ? data.assignments : [];
    if (assignments.length === 0) return null;

    // POS is date-based, so compare by day boundaries
    const txDate = toJsDate(transactionDate);
    if (!txDate) return null;
    const tx = startOfDay(txDate);

    // Sort assignments by startDate ascending (safety)
    const sorted = [...assignments].sort((a, b) => {
      const da = toJsDate(a.startDate);
      const db = toJsDate(b.startDate);
      return (da?.getTime?.() || 0) - (db?.getTime?.() || 0);
    });

    for (const a of sorted) {
      const s = toJsDate(a.startDate);
      if (!s) continue;

      const e = a.endDate ? toJsDate(a.endDate) : null;

      const start = startOfDay(s);
      const end = e ? endOfDay(e) : null;

      if (tx >= start && (!end || tx <= end)) {
        return a;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

/* ------------------------------------------------------------------
   Returns ONLY vehicleId
------------------------------------------------------------------- */
export async function getVehicleForTerminal(terminalId, transactionDate) {
  if (!terminalId || !transactionDate) return null;

  try {
    // Try NEW assignment system first
    const assignment = await getAssignmentForDate(terminalId, transactionDate);
    if (assignment?.vehicleId) return assignment.vehicleId;

    // Fallback to OLD mapping
    return await getVehicleFromOldMapping(terminalId);
  } catch (error) {
    return null;
  }
}

/* ------------------------------------------------------------------
   OLD MAPPING FALLBACK
------------------------------------------------------------------- */
async function getVehicleFromOldMapping(terminalId) {
  try {
    const terminalKey = String(terminalId).trim();

    const q = query(
      collection(db, "posDeviceMappings"),
      where("terminalId", "==", terminalKey)
    );

    const snap = await getDocs(q);

    if (!snap.empty) {
      const oldMap = snap.docs[0].data();
      return oldMap.vehicleId || null;
    }

    return null;
  } catch (error) {
    return null;
  }
}
